'use client';
import React, { memo, useMemo, useRef } from 'react';
import CountUp from 'react-countup';

import { ITypo } from '@/app/_components/common';
import { Wrapper } from '@/app/_components/layout';
import {
	CrownIcon,
	DotTable,
	RankingIcon,
	TimerIcon,
} from '@/app/assets/Icons';
import { MedalStarIcon } from '@/app/assets/Icons/home';
import { useGetImpressivesQuery } from '@/store/api/impressive.api';
import { getQueryString } from '@/utils';

import { useInView } from 'motion/react';

const ImpressiveSection = () => {
	const { data } = useGetImpressivesQuery(getQueryString());
	const impressives = useMemo(() => data?.data || [], [data]);
	const ref = useRef(null);
	const isInView = useInView(ref);
	return (
		<div className="relative overflow-hidden">
			<Wrapper className="col lg:row px-3 py-14 sm:py-14 lg:py-[100px]">
				<div className="col relative gap-4">
					<div className="bg-silver absolute -right-4 -top-8 -z-[1] h-[84px] w-[94px] rounded-ss-[60px] sm:-right-10 lg:-top-[60px] lg:left-[72px] lg:size-[178px] lg:rounded-ss-[100px]" />
					<ITypo
						size={20}
						as="h3"
						weight="semi"
						className="sm:text-40 text-center uppercase lg:text-left"
					>
						<p>NHỮNG CON SỐ</p>
						<p>ẤN TƯỢNG</p>
					</ITypo>
					<ITypo
						size={14}
						as="p"
						className="text-gray lg:text-16 sm:text-16 max-w-[475px] text-justify lg:-mt-3"
					>
						Là thành quả tự hào của quá trình nghiên cứu, thử nghiệm
						và đồng hành tạo ra giá trị riêng cho người học trên bản
						đồ giáo dục Việt Nam.
					</ITypo>

					<DotTable className="text-mauvelous mx-auto -mt-2 h-[58px] w-[150px] shrink-0 lg:mt-11 lg:h-[154px] lg:w-[405px]" />
				</div>

				<div className="relative w-full">
					<div className="bg-mauvelous absolute -inset-x-4 bottom-8 top-11 -z-[1] max-h-[620px] rounded-ss-[150px] sm:-inset-x-10 lg:bottom-0 lg:left-auto lg:top-[140px] lg:w-full lg:max-w-[882px]" />
					<div className="border-bittersweet absolute bottom-2 right-2 -z-[2] size-[62px] rounded-full border-[6px] lg:-right-0 lg:bottom-16 lg:size-[120px] lg:border-[8.8px]" />

					<div className="*:col *:border-silver *:shadow-2 mt-3 grid max-w-[650px] grid-cols-2 grid-rows-9 gap-x-5 gap-y-4 *:items-center *:rounded-[40px] *:border *:bg-white *:px-4 *:py-5 *:text-center lg:ml-auto lg:mr-10 lg:gap-x-8 lg:gap-y-[30px] lg:*:px-8 lg:*:pb-12 lg:*:pt-10">
						<div className="row-span-4 row-start-2">
							<div className="center bg-blue-alice size-[66px] rounded-[20px] lg:size-[121px]">
								<CrownIcon className="text-blue size-10 shrink-0 stroke-[3] lg:size-[73px] lg:stroke-[4]" />
							</div>
							<div className="lg:text-32 mb-2 mt-4 font-bold lg:mb-3 lg:mt-8">
								<CountUp
									prefix={impressives?.[0]?.label}
									className="lg:text-32 mb-2 mt-4 font-bold lg:mb-3 lg:mt-8"
									end={impressives?.[0]?.value || 1}
									duration={4}
									enableScrollSpy
								/>
							</div>
							<ITypo
								size={12}
								as="p"
								className="text-gray lg:text-16"
							>
								{impressives?.[0]?.content}
							</ITypo>
						</div>
						<div className="col-start-2 row-span-4">
							<div className="center bg-yellow-light size-[66px] rounded-[20px] lg:size-[121px]">
								<MedalStarIcon className="text-yellow size-10 shrink-0 stroke-[3] lg:size-[73px] lg:stroke-[4]" />
							</div>
							<div className="lg:text-32 mb-2 mt-4 font-bold lg:mb-3 lg:mt-8">
								<CountUp
									prefix={impressives?.[1]?.label || ''}
									className="lg:text-32 mb-2 mt-4 font-bold lg:mb-3 lg:mt-8"
									end={impressives?.[1]?.value || 1}
									duration={4}
									enableScrollSpy
								/>
							</div>
							<ITypo
								size={12}
								as="p"
								className="text-gray lg:text-16"
							>
								{impressives?.[1]?.content}
							</ITypo>
						</div>
						<div className="row-span-4 row-start-6">
							<div className="center bg-red-light size-[66px] rounded-[20px] lg:size-[121px]">
								<TimerIcon className="text-pink size-10 shrink-0 stroke-[3] lg:size-[73px] lg:stroke-[4]" />
							</div>
							<div className="lg:text-32 mb-2 mt-4 font-bold lg:mb-3 lg:mt-8">
								<CountUp
									prefix={impressives?.[2]?.label || ''}
									className="lg:text-32 mb-2 mt-4 font-bold lg:mb-3 lg:mt-8"
									end={impressives?.[2]?.value || 1}
									duration={4}
									enableScrollSpy
								/>
							</div>
							<ITypo
								size={12}
								as="p"
								className="text-gray lg:text-16"
							>
								{impressives?.[2]?.content || ''}
							</ITypo>
						</div>
						<div className="col-start-2 row-span-4 row-start-5">
							<div className="center bg-green-light size-[66px] rounded-[20px] lg:size-[121px]">
								<RankingIcon className="text-green size-10 shrink-0 stroke-[3] lg:size-[73px] lg:stroke-[4]" />
							</div>
							<div className="lg:text-32 mb-2 mt-4 font-bold lg:mb-3 lg:mt-8">
								<CountUp
									prefix={impressives?.[3]?.label || ''}
									className="lg:text-32 mb-2 mt-4 font-bold lg:mb-3 lg:mt-8"
									end={impressives?.[3]?.value || 1}
									duration={4}
									enableScrollSpy
								/>
							</div>
							<ITypo
								size={12}
								as="p"
								className="text-gray lg:text-16"
							>
								{impressives?.[3]?.content}
							</ITypo>
						</div>
					</div>
				</div>
			</Wrapper>
		</div>
	);
};

export default memo(ImpressiveSection);
